/**
 * Payment configuration management page.
 * Supports channel-specific forms and sensitive field masking.
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'

interface PaymentConfig {
  id: number
  name: string
  channel_type: string
  is_active: string
  created_at: string
  config?: Record<string, string>
}

interface Application {
  id: number
  name: string
}

type SupportedChannelType = 'wechat_pay' | 'alipay' | 'stripe' | 'stripe_checkout' | 'nowpayments' | 'airwallex' | 'paykka'

interface ChannelField {
  key: string
  label: string
  type: 'text' | 'password' | 'textarea'
  sensitive?: boolean
  placeholder?: string
  required?: boolean
}

// Channel-specific form field definitions (Property 5)
const CHANNEL_FIELDS: Record<SupportedChannelType, ChannelField[]> = {
  wechat_pay: [
    { key: 'app_id', label: 'App ID', type: 'text', required: true, placeholder: 'wx...' },
    { key: 'mch_id', label: 'Merchant ID', type: 'text', required: true },
    { key: 'api_v3_key', label: 'API v3 Key', type: 'password', sensitive: true, required: true },
    { key: 'serial_no', label: 'Certificate Serial No', type: 'text', required: true },
    { key: 'private_key', label: 'Private Key', type: 'textarea', sensitive: true, required: true },
  ],
  alipay: [
    { key: 'app_id', label: 'App ID', type: 'text', required: true },
    { key: 'private_key', label: 'Private Key', type: 'textarea', sensitive: true, required: true },
    { key: 'public_key', label: 'Alipay Public Key', type: 'textarea', sensitive: true, required: true },
    { key: 'sign_type', label: 'Sign Type', type: 'text', placeholder: 'RSA2' },
  ],
  stripe: [
    { key: 'api_key', label: 'Secret Key', type: 'password', sensitive: true, required: true, placeholder: 'sk_...' },
    { key: 'publishable_key', label: 'Publishable Key', type: 'text', placeholder: 'pk_...' },
    { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', sensitive: true, required: true, placeholder: 'whsec_...' },
  ],
  nowpayments: [
    { key: 'api_key', label: 'API Key', type: 'password', sensitive: true, required: true, placeholder: 'NOWPayments API key' },
    { key: 'ipn_secret', label: 'IPN Secret', type: 'password', sensitive: true, required: true, placeholder: 'NOWPayments IPN secret' },
    { key: 'base_url', label: 'API Base URL', type: 'text', placeholder: 'https://api.nowpayments.io/v1' },
    { key: 'pay_currency', label: 'Default Crypto Currency', type: 'text', placeholder: 'Optional, e.g. usdttrc20' },
  ],
  airwallex: [
    { key: 'client_id', label: 'Client ID', type: 'password', sensitive: true, required: true, placeholder: 'Airwallex Client ID' },
    { key: 'api_key', label: 'API Key', type: 'password', sensitive: true, required: true, placeholder: 'Airwallex API key' },
    { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', sensitive: true, required: true, placeholder: 'Airwallex webhook signing secret' },
    { key: 'environment', label: 'Environment', type: 'text', placeholder: 'demo or production' },
    { key: 'base_url', label: 'API Base URL', type: 'text', placeholder: 'https://api-demo.airwallex.com/api/v1' },
  ],
  // add-paykka-payments: RSA key material, so private/public keys use
  // multi-line textarea inputs (the portal hands out Base64 DER blobs).
  paykka: [
    { key: 'app_id', label: 'App ID', type: 'text', required: true, placeholder: 'PayKKa portal AppId' },
    { key: 'merchant_id', label: 'Merchant ID', type: 'text', required: true, placeholder: 'PayKKa merchant id' },
    { key: 'private_key', label: 'Merchant Private Key', type: 'textarea', sensitive: true, required: true, placeholder: 'Base64 DER (PKCS#8) or PEM' },
    { key: 'paykka_public_key', label: 'PayKKa Public Key', type: 'textarea', sensitive: true, required: true, placeholder: 'Base64 DER (X.509 SPKI) or PEM' },
    { key: 'environment', label: 'Environment', type: 'text', placeholder: 'sandbox or production' },
    { key: 'base_url', label: 'API Base URL', type: 'text', placeholder: 'https://openapi-sandbox.paykka.com' },
    // Conditionally required: PayKKa demands a line item, so one of these two
    // must exist -- either sent per request (channel_params.goods /
    // return_url) or defaulted here. Not marked `required` because a caller
    // that always sends its own would be wrongly blocked; the label carries
    // the condition instead. Omitting both fails at payment time with
    // CONFIGURATION_ERROR, not at save time.
    { key: 'default_goods_link', label: 'Default Goods Link (required unless sent per request)', type: 'text', placeholder: 'https://your-site.com/product' },
    { key: 'default_return_url', label: 'Default Return URL (required unless sent per request)', type: 'text', placeholder: 'https://your-site.com/return' },
    { key: 'session_expire_minutes', label: 'Cashier Expiry (minutes, max 1440)', type: 'text', placeholder: '120' },
  ],
  stripe_checkout: [
    { key: 'api_key', label: 'Secret Key', type: 'password', sensitive: true, required: true, placeholder: 'sk_live_... or sk_test_...' },
    { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', sensitive: true, required: true, placeholder: 'whsec_...' },
    { key: 'default_success_url', label: 'Default Success URL', type: 'text', placeholder: 'https://your-site.com/success' },
    { key: 'default_cancel_url', label: 'Default Cancel URL', type: 'text', placeholder: 'https://your-site.com/cancel' },
  ],
}

const CHANNEL_TYPE_LABELS: Record<string, string> = {
  wechat_pay: 'WeChat Pay',
  alipay: 'Alipay',
  stripe: 'Stripe',
  stripe_checkout: 'Stripe Checkout',
  nowpayments: 'NOWPayments Crypto',
  airwallex: 'Airwallex',
  paykka: 'PayKKa',
  paypal: 'PayPal (Unsupported)',
}

// Channel types that support the /test endpoint
const TESTABLE_CHANNEL_TYPES: Set<string> = new Set(['stripe', 'stripe_checkout'])

const SUPPORTED_CHANNEL_OPTIONS: Array<{
  value: SupportedChannelType
  label: string
}> = [
  { value: 'wechat_pay', label: CHANNEL_TYPE_LABELS.wechat_pay },
  { value: 'alipay', label: CHANNEL_TYPE_LABELS.alipay },
  { value: 'stripe', label: CHANNEL_TYPE_LABELS.stripe },
  { value: 'stripe_checkout', label: CHANNEL_TYPE_LABELS.stripe_checkout },
  { value: 'nowpayments', label: CHANNEL_TYPE_LABELS.nowpayments },
  { value: 'airwallex', label: CHANNEL_TYPE_LABELS.airwallex },
  // add-paykka-payments task 9.7 / Migration Plan step 4: PayKKa is NOT
  // offered in the picker until the real-environment acceptance suite
  // (9.1-9.6) has passed. The field definitions and label above stay so an
  // allowlisted pilot config still renders and edits correctly; the
  // authoritative refusal is the server-side launch gate
  // (backend/app/payment/launch_gate.py), not this list.
]

// Sensitive field masking (Property 6)
function maskSensitiveValue(value: string): string {
  if (!value || value.length < 8) return '••••••••'
  return value.substring(0, 4) + '••••••••' + value.substring(value.length - 4)
}

// Sensitive field input with reveal toggle
function SensitiveInput({
  id,
  value,
  onChange,
  placeholder,
  isTextarea = false,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  isTextarea?: boolean
}) {
  const [revealed, setRevealed] = useState(false)

  if (isTextarea) {
    return (
      <div className="relative">
        <textarea
          id={id}
          value={revealed ? value : (value ? maskSensitiveValue(value) : '')}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setRevealed(true)}
          placeholder={placeholder}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-2 top-2"
          onClick={() => setRevealed(!revealed)}
        >
          {revealed ? 'Hide' : 'Show'}
        </Button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Input
        id={id}
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-16 font-mono"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
        onClick={() => setRevealed(!revealed)}
      >
        {revealed ? 'Hide' : 'Show'}
      </Button>
    </div>
  )
}

export default function PaymentConfigsPage() {
  const { appId } = useParams<{ appId: string }>()
  const [configs, setConfigs] = useState<PaymentConfig[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<PaymentConfig | null>(null)
  const [selectedAppId, setSelectedAppId] = useState<string>('')
  const [formData, setFormData] = useState<{
    name: string
    channel_type: SupportedChannelType | ''
    config: Record<string, string>
  }>({
    name: '',
    channel_type: '',
    config: {},
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTesting, setIsTesting] = useState<number | null>(null)
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  const [allOwnerConfigs, setAllOwnerConfigs] = useState<PaymentConfig[]>([])
  const [linkSelectedIds, setLinkSelectedIds] = useState<Set<number>>(new Set())
  const [linkLoading, setLinkLoading] = useState(false)
  const { toast } = useToast()

  // Fetch applications list when not in app-specific mode
  const fetchApplications = async () => {
    if (appId) return // Don't fetch if we already have appId from URL
    try {
      const response = await api.get<Application[]>('/applications')
      setApplications(response.data)
    } catch {
      // Silently fail - applications list is optional
    }
  }

  const fetchConfigs = async () => {
    try {
      setLoading(true)
      const endpoint = appId
        ? `/applications/${appId}/payment-configs`
        : '/payment-configs'
      const response = await api.get<PaymentConfig[]>(endpoint)
      setConfigs(response.data)
      setError(null)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message)
      } else {
        setError('Failed to fetch payment configurations')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchConfigs()
    fetchApplications()
  }, [appId])

  const handleCreate = async () => {
    // Validate application selection when not using app-specific route
    if (!appId && !selectedAppId) {
      toast({ title: 'Error', description: 'Please select an application', variant: 'destructive' })
      return
    }
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Configuration name is required', variant: 'destructive' })
      return
    }
    if (!formData.channel_type) {
      toast({ title: 'Error', description: 'Please select a payment channel', variant: 'destructive' })
      return
    }

    // Validate required fields
    const fields = CHANNEL_FIELDS[formData.channel_type]
    for (const field of fields) {
      if (field.required && !formData.config[field.key]?.trim()) {
        toast({ title: 'Error', description: `${field.label} is required`, variant: 'destructive' })
        return
      }
    }

    setIsSubmitting(true)
    try {
      const endpoint = appId
        ? `/applications/${appId}/payment-configs`
        : '/payment-configs'
      const payload: Record<string, unknown> = {
        name: formData.name,
        channel_type: formData.channel_type,
        config: formData.config,
      }
      // Add application_ids when using global endpoint
      if (!appId && selectedAppId) {
        payload.application_ids = [parseInt(selectedAppId, 10)]
      }
      await api.post(endpoint, payload)
      toast({ title: 'Success', description: 'Payment configuration created', variant: 'success' })
      setIsCreateOpen(false)
      setFormData({ name: '', channel_type: '', config: {} })
      setSelectedAppId('')
      fetchConfigs()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to create configuration',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedConfig) return

    setIsSubmitting(true)
    try {
      const endpoint = appId
        ? `/applications/${appId}/payment-configs/${selectedConfig.id}`
        : `/payment-configs/${selectedConfig.id}`
      await api.delete(endpoint)
      toast({ title: 'Success', description: 'Configuration deleted', variant: 'success' })
      setIsDeleteOpen(false)
      setSelectedConfig(null)
      fetchConfigs()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to delete configuration',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (config: PaymentConfig) => {
    try {
      const endpoint = appId
        ? `/applications/${appId}/payment-configs/${config.id}`
        : `/payment-configs/${config.id}`
      await api.put(endpoint, {
        is_active: config.is_active === 'Y' ? 'N' : 'Y',
      })
      toast({
        title: 'Success',
        description: `Configuration ${config.is_active === 'Y' ? 'deactivated' : 'activated'}`,
        variant: 'success',
      })
      fetchConfigs()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to update configuration',
        variant: 'destructive',
      })
    }
  }

  const handleTest = async (config: PaymentConfig) => {
    setIsTesting(config.id)
    try {
      const endpoint = appId
        ? `/applications/${appId}/payment-configs/${config.id}/test`
        : `/payment-configs/${config.id}/test`
      const response = await api.post<{
        success: boolean
        message: string
        details?: { livemode?: boolean; account_mode?: string; account_id?: string; error_category?: string }
      }>(endpoint, {})
      const details = response.data.details || {}
      let description = response.data.message
      if (response.data.success && details.account_mode) {
        description += ` (${details.account_mode} mode)`
      }
      if (response.data.success && details.livemode === false) {
        description += ' — test key'
      }
      toast({
        title: response.data.success ? 'Test passed' : 'Test failed',
        description,
        variant: response.data.success ? 'success' : 'destructive',
      })
    } catch (err) {
      toast({
        title: 'Test failed',
        description: err instanceof ApiRequestError ? err.message : 'Connection test failed',
        variant: 'destructive',
      })
    } finally {
      setIsTesting(null)
    }
  }

  const handleConfigFieldChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    })
  }

  const handleOpenLinkDialog = async () => {
    setIsLinkOpen(true)
    setLinkLoading(true)
    try {
      const [allRes, linkedRes] = await Promise.all([
        api.get<PaymentConfig[]>('/payment-configs', { per_page: 100 }),
        api.get<PaymentConfig[]>(`/applications/${appId}/payment-configs`, { per_page: 100 }),
      ])
      setAllOwnerConfigs(allRes.data)
      setLinkSelectedIds(new Set(linkedRes.data.map((c) => c.id)))
    } catch {
      setAllOwnerConfigs([])
      setLinkSelectedIds(new Set())
    } finally {
      setLinkLoading(false)
    }
  }

  const handleLinkSave = async () => {
    setLinkLoading(true)
    try {
      await api.put(`/applications/${appId}/payment-configs`, {
        payment_config_ids: Array.from(linkSelectedIds),
      })
      toast({ title: 'Success', description: 'Payment config links updated', variant: 'success' })
      setIsLinkOpen(false)
      fetchConfigs()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to update links',
        variant: 'destructive',
      })
    } finally {
      setLinkLoading(false)
    }
  }

  const toggleLinkConfig = (configId: number) => {
    setLinkSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(configId)) {
        next.delete(configId)
      } else {
        next.add(configId)
      }
      return next
    })
  }

  return (
    <div>
      <div className="mb-8">
        {appId && (
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/applications">Back to Applications</Link>
          </Button>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Payment Configurations</h1>
            <p className="text-muted-foreground mt-2">
              Manage payment channel configurations
            </p>
          </div>
          <div className="flex gap-2">
            {appId && (
              <Button variant="outline" onClick={handleOpenLinkDialog}>
                Link Existing
              </Button>
            )}
            <Button onClick={() => setIsCreateOpen(true)}>Add Configuration</Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchConfigs}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-4">
              No payment configurations yet
            </p>
            <Button onClick={() => setIsCreateOpen(true)}>
              Add your first payment configuration
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {configs.map((config) => (
            <Card key={config.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {config.name}
                  <Badge
                    variant={config.is_active === 'Y' ? 'success' : 'secondary'}
                  >
                    {config.is_active === 'Y' ? 'Active' : 'Inactive'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {CHANNEL_TYPE_LABELS[config.channel_type] || config.channel_type}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Created: {new Date(config.created_at).toLocaleDateString()}
                </p>
                <div className="flex flex-wrap gap-2">
                  {TESTABLE_CHANNEL_TYPES.has(config.channel_type) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(config)}
                      disabled={isTesting === config.id}
                    >
                      {isTesting === config.id ? 'Testing...' : 'Test'}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(config)}
                  >
                    {config.is_active === 'Y' ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setSelectedConfig(config)
                      setIsDeleteOpen(true)
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Payment Configuration</DialogTitle>
            <DialogDescription>
              Configure a new payment channel. Sensitive fields are masked for security.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Application selector - only shown when not in app-specific mode */}
            {!appId && (
              <div className="space-y-2">
                <Label>
                  Application
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Select value={selectedAppId} onValueChange={setSelectedAppId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an application" />
                  </SelectTrigger>
                  <SelectContent>
                    {applications.map((app) => (
                      <SelectItem key={app.id} value={String(app.id)}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {applications.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No applications found.{' '}
                    <Link to="/applications" className="text-primary underline">
                      Create one first
                    </Link>
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Configuration Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production WeChat Pay"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Channel</Label>
              <Select
                value={formData.channel_type}
                onValueChange={(value: SupportedChannelType) =>
                  setFormData({ ...formData, channel_type: value, config: {} })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a payment channel" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CHANNEL_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Channel-specific fields (Property 5) */}
            {formData.channel_type && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">
                  {CHANNEL_TYPE_LABELS[formData.channel_type]} Configuration
                </h4>
                {CHANNEL_FIELDS[formData.channel_type].map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key}>
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {field.sensitive ? (
                      <SensitiveInput
                        id={field.key}
                        value={formData.config[field.key] || ''}
                        onChange={(value) => handleConfigFieldChange(field.key, value)}
                        placeholder={field.placeholder}
                        isTextarea={field.type === 'textarea'}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={field.key}
                        value={formData.config[field.key] || ''}
                        onChange={(e) => handleConfigFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    ) : (
                      <Input
                        id={field.key}
                        type={field.type}
                        value={formData.config[field.key] || ''}
                        onChange={(e) => handleConfigFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Existing Config Dialog */}
      <Dialog open={isLinkOpen} onOpenChange={setIsLinkOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Link Existing Configs</DialogTitle>
            <DialogDescription>
              Select payment configurations to link to this application.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {linkLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : allOwnerConfigs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No payment configs available. Create one first.
              </p>
            ) : (
              <div className="max-h-60 overflow-y-auto rounded-md border border-input">
                {allOwnerConfigs.map((config) => (
                  <button
                    key={config.id}
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                    onClick={() => toggleLinkConfig(config.id)}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        linkSelectedIds.has(config.id)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input'
                      }`}
                    >
                      {linkSelectedIds.has(config.id) && (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className="flex-1 truncate">{config.name}</span>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {CHANNEL_TYPE_LABELS[config.channel_type] || config.channel_type}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLinkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleLinkSave} disabled={linkLoading || allOwnerConfigs.length === 0}>
              {linkLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Configuration</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedConfig?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
